import { http } from "./client";
import type { PageMeta, PageParams } from "@/types/api";
import type {
  AttendanceLog,
  AttendanceRecord,
  AttendanceSource,
  AttendanceStatus,
  AttendanceSummary,
  Break,
  BreakSummary,
  HRAttendanceRoster,
  LeaveEligibility,
} from "@/types/domain";

export interface AttendanceFilters extends PageParams {
  from?: string;
  to?: string;
  employee_id?: string;
  department_id?: string;
  branch_id?: string;
  /** The shift the day was worked on: days whose shift window is that shift's. */
  shift_id?: string;
  status?: AttendanceStatus | "";
  source?: AttendanceSource | "";
  late?: boolean;
  overtime?: boolean;
  exceptions?: boolean;
}

/** One sheet row the import refused, and why. */
export interface AttendanceImportIssue {
  line: number;
  employee_code: string;
  date: string;
  message: string;
}

/** A valid sheet row as it will be imported. */
export interface AttendanceImportRow {
  line: number;
  employee_code: string;
  employee_name: string;
  date: string;
  /** punches: In/Out recalculated by the shift rules; status: a manual correction. */
  mode: "punches" | "status";
  status?: string;
  in?: string;
  out?: string;
  remarks?: string;
}

/** POST /attendance/import - a check (dry run) or an import. */
export interface AttendanceImportResult {
  dry_run: boolean;
  total: number;
  valid: number;
  imported: number;
  errors: AttendanceImportIssue[];
  rows: AttendanceImportRow[];
  from?: string;
  to?: string;
}

/** Employee-days of a filtered listing over a date range (GET /attendance/summary?from=&to=). */
export interface AttendanceRangeSummary {
  days: number;
  employees: number;
  present: number;
  absent: number;
  leave: number;
  half_day: number;
  on_duty: number;
  weekly_off: number;
  holiday: number;
  late: number;
  early_exit: number;
  missing_punch: number;
  worked_minutes: number;
  break_minutes: number;
  overtime_minutes: number;
}

export interface MarkAttendanceInput {
  employee_id: string;
  date: string;
  status: AttendanceStatus;
  remarks: string;
  first_in?: string;
  last_out?: string;
  payable_units?: number;
  overtime_minutes?: number;
  // How long the employee was on break that day - the only way to record
  // break time for a manually-corrected day, since there is no real
  // Break-System punch data to derive it from otherwise.
  break_minutes?: number;
}

export interface RecalculateResult {
  range: { from: string; to: string };
  employee_count: number;
  days_evaluated: number;
  records_written: number;
  records_skipped: number;
  /** True when the requested range's end fell after today and was narrowed
   * down to it - a future date is never evaluated, however far the caller's
   * own `to` reached. `range.to` above already reflects the actual (possibly
   * narrowed) end date that was used. */
  clamped_to_today: boolean;
  elapsed_ms: number;
}

export interface SelfPunchInput {
  latitude: number;
  longitude: number;
  accuracy_meters?: number;
  /** "Why are you working today" - only meaningful (and only ever required
   * by the backend) for a check-in on an applicable holiday; ignored for a
   * checkout and for any ordinary working-day check-in. */
  reason?: string;
}

export interface SelfPunchOutcome {
  attendance_log_id: string;
  /** The SERVER's timestamp for the punch. The client clock is never used to
   * record or display when a punch happened - this is what the UI shows. */
  punch_time: string;
  /** The tenant-local calendar day the punch was attributed to, resolved
   * server-side, so employee and Tenant Admin always agree on the day. */
  business_date: string;
  /** True when the request repeated a punch already recorded seconds ago (a
   * double tap, a retry). Still a success - `attendance` below is the real,
   * existing record. */
  duplicate?: boolean;
  attendance_id?: string;
  status?: AttendanceStatus;
  /** The canonical settled attendance record this punch produced, read back
   * from the database inside the writing transaction. Employee UIs render
   * check-in/check-out/working time from THIS, never by deriving them from
   * the punch list, so they show the same row the Tenant Admin's attendance
   * table does. */
  attendance?: AttendanceRecord;
  location_verified?: boolean;
  within_geofence?: boolean;
  distance_meters?: number;
  accuracy_meters?: number;
  geo_fence_id?: string;
}

/** What Start/End Break return - the break itself, plus the canonical
 * settled attendance record it affected. The employee's UI renders
 * break_minutes/breaks_taken/worked_minutes from THIS, exactly like
 * SelfPunchOutcome's own `attendance` field, never re-deriving them
 * itself. */
export interface BreakOutcome {
  break: Break;
  attendance?: AttendanceRecord;
}

// Machine-readable rejection reasons the backend returns in error.details.reason
// when a self-service punch fails geo-validation (Phase 3) - see
// AttendanceService.validateGeoFence.
export type GeoFenceRejectionReason = "low_accuracy" | "no_geo_fence_configured" | "outside_geo_fence";

/** How one employee marks attendance. Resolved server-side through the
 * tenant -> branch -> department -> employee hierarchy; the client never
 * decides a mode for itself, it only renders the one it is told. */
export type AttendanceMode = "normal" | "geo_fencing" | "field_check_in" | "check_in_off";

/** Which level of the configuration hierarchy an effective mode came from. */
export type AttendanceModeLevel = "employee" | "department" | "branch" | "tenant";

/** One employee's attendance-mode configuration: the mode in force, where it
 * came from, and what is set at every level - so an admin can see WHY someone
 * is in the mode they are in before changing it. */
export interface EmployeeAttendanceSettings {
  employee_id: string;
  employee_code: string;
  employee_name: string;
  mode: AttendanceMode;
  resolved_from: AttendanceModeLevel;
  tenant_default: AttendanceMode;
  branch_default?: AttendanceMode;
  department_default?: AttendanceMode;
  /** The employee-level override. Absent means "inherit" - which is a real
   * choice in the UI, not the absence of one. */
  employee_override?: AttendanceMode;
  branch_name?: string;
  department_name?: string;
  available_modes: AttendanceMode[];
}

/** A captured geo-tag. Only ever what a punch actually recorded - never an
 * interpolated or smoothed position. */
export interface FieldLocation {
  latitude: number;
  longitude: number;
  accuracy_meters?: number;
  recorded_at: string;
}

export type FieldWorkStatus = "not_started" | "working" | "completed";

/** One row of the Field Workforce listing (or one day of an employee's field
 * activity - the same shape backs both). */
export interface FieldWorkforceEntry {
  employee_id: string;
  employee_code: string;
  employee_name: string;
  designation?: string;
  department?: string;
  branch?: string;
  resolved_from: AttendanceModeLevel;
  status: FieldWorkStatus;
  check_in_time?: string;
  check_in_location?: FieldLocation;
  check_out_time?: string;
  check_out_location?: FieldLocation;
  /** The most recent geo-tag captured on this date. NOT live tracking: a
   * location is captured at check-in and at check-out and at no other time,
   * so for someone still working this is their check-in location. */
  last_known_location?: FieldLocation;
  last_updated_at?: string;
  /** Settled worked time from the canonical attendance record the existing
   * engine derives - never recomputed in the browser. */
  worked_minutes?: number;
  attendance_status?: AttendanceStatus;
}

export interface FieldWorkforceResult {
  date: string;
  summary: {
    total_field_employees: number;
    not_started: number;
    working: number;
    completed: number;
  };
  employees: { items: FieldWorkforceEntry[]; page: PageMeta };
}

export interface FieldEmployeeActivity {
  employee_id: string;
  employee_code: string;
  employee_name: string;
  mode: AttendanceMode;
  range: { from: string; to: string };
  days: FieldWorkforceEntry[];
}

/** One recorded GPS-tagged punch belonging to a settled attendance day -
 * either `primary` (the exact punch the check-in time was derived from) or
 * one of `other_punches` on a CheckInLocation. */
export interface CheckInPunch {
  attendance_log_id: string;
  punch_time: string;
  direction: "in" | "out" | "unknown";
  source: AttendanceSource;
  latitude?: number;
  longitude?: number;
  accuracy_meters?: number;
  within_geofence?: boolean;
  distance_meters?: number;
  geo_fence_id?: string;
}

/** "View on Map"'s entire read contract - the recorded GPS evidence behind
 * one settled attendance record's check-in. `primary` is only ever the exact
 * punch the engine derived the settled check-in time from, AND only when
 * that punch itself carries coordinates: a GPS-less biometric punch, or a
 * manually-corrected time, has `has_check_in=true` but `primary=undefined`
 * rather than a fabricated or substituted location - so when `primary` IS
 * present, its latitude/longitude are guaranteed real numbers. */
export interface CheckInLocation {
  attendance_id: string;
  employee_id: string;
  employee_name: string;
  employee_code: string;
  date: string;
  has_check_in: boolean;
  check_in_time?: string;
  primary?: CheckInPunch & { latitude: number; longitude: number };
  other_punches?: CheckInPunch[];
}

export const attendanceApi = {
  list: (filters: AttendanceFilters = {}) => http.getPage<AttendanceRecord>("/attendance", filters),
  exceptions: (filters: AttendanceFilters = {}) => http.getPage<AttendanceRecord>("/attendance/exceptions", filters),
  /** Aggregates per employee for a whole month (month-to-date once it
   * reaches the current month). Kept for the Attendance Report/monthly
   * views; the dashboard's daily stat tiles use `dailySummary` below
   * instead. */
  summary: (period?: string) =>
    http.get<{ period: string; summaries: Record<string, AttendanceSummary>; break_summaries: Record<string, BreakSummary> }>(
      "/attendance/summary",
      { period },
    ),
  /** Aggregates per employee for exactly one date - "how many present/
   * absent/on leave/late TODAY", not "so far this month". A future date
   * returns empty summaries, never a computed one (see
   * AttendanceService.dayRangeClampedToToday). */
  /** The headcount of exactly what the list shows: the same range and filters, paging ignored. */
  rangeSummary: (filters: AttendanceFilters) =>
    http.get<{ from: string; to: string; summary: AttendanceRangeSummary }>("/attendance/summary", {
      ...filters,
      page: undefined,
      per_page: undefined,
    }),
  /** Checks (dryRun) or imports an earlier-attendance sheet (CSV or Excel). */
  importSheet: (file: File, dryRun: boolean) => {
    const form = new FormData();
    form.append("file", file);
    form.append("dry_run", String(dryRun));
    return http.post<AttendanceImportResult>("/attendance/import", form);
  },
  importTemplate: (format: "csv" | "xlsx") => http.download("/attendance/import/template", { format }),
  /** The filtered records as a file - the same range and filters as the list (needs report.export). */
  exportRecords: (filters: AttendanceFilters, format: "csv" | "xlsx") =>
    http.download("/attendance/export", { ...filters, page: undefined, per_page: undefined, format }),
  dailySummary: (date: string) =>
    http.get<{
      date: string;
      summaries: Record<string, AttendanceSummary>;
      break_summaries: Record<string, BreakSummary>;
      /** The canonical day headcount (same one the HR dashboard uses). Absent
       * when the caller cannot list employees - fall back to `summaries`. */
      roster?: HRAttendanceRoster;
    }>(
      "/attendance/summary",
      { date },
    ),
  forEmployee: (employeeId: string, from?: string, to?: string) =>
    http.get<{
      range: { from: string; to: string };
      records: AttendanceRecord[];
      summary: AttendanceSummary;
      // Employee-wise Break History (section 14) "Monthly Total" row -
      // backend-aggregated over the SAME range, never summed from `records`
      // here (see AttendanceService.BreakSummary).
      break_summary: BreakSummary;
      // Always for TODAY specifically (server-resolved, tenant timezone),
      // independent of from/to - see AttendanceService.LeaveEligibilityToday.
      leave_eligibility: LeaveEligibility;
    }>(`/attendance/employee/${employeeId}`, { from, to }),
  mark: (input: MarkAttendanceInput) => http.post<AttendanceRecord>("/attendance/mark", input),
  clearOverride: (id: string) => http.delete<{ message: string }>(`/attendance/${id}/override`),
  recalculate: (from: string, to: string, employee_ids?: string[]) =>
    http.post<RecalculateResult>("/attendance/recalculate", { from, to, employee_ids }),

  logs: (filters: AttendanceFilters & { biometric_code?: string; device_id?: string; unmapped?: boolean } = {}) =>
    http.getPage<AttendanceLog>("/attendance/logs", filters),
  unmappedLogs: (params: PageParams = {}) => http.getPage<AttendanceLog>("/attendance/logs/unmapped", params),
  remapLogs: (biometric_code: string, employee_id: string) =>
    http.post<{ remapped_punches: number; message: string }>("/attendance/logs/remap", { biometric_code, employee_id }),

  // ---- Break System ----
  // "Daily Break Details" (section 11) - every break tied to one settled
  // attendance day, oldest first. Gated by attendance.read, the same
  // permission every other operational attendance read uses.
  attendanceBreaks: (attendanceId: string) => http.get<{ items: Break[] }>(`/attendance/${attendanceId}/breaks`),

  // "View on Map" - the recorded GPS evidence behind one settled attendance
  // record's check-in. Shared by admin/HR/Manager (attendance.read, scoped
  // exactly like the list) and Employee Self Service (attendance.self_read,
  // own record only) - the backend splits access internally, so this one
  // call works from either surface.
  checkInLocation: (attendanceId: string) => http.get<CheckInLocation>(`/attendance/${attendanceId}/location`),

  // Self-service GPS check-in/check-out - the employee is always derived
  // server-side from the authenticated session (attendance.self_mark), never
  // sent in this payload, which carries only what the device measured.
  checkIn: (input: SelfPunchInput) => http.post<SelfPunchOutcome>("/employee/self/attendance/check-in", input),
  checkOut: (input: SelfPunchInput) => http.post<SelfPunchOutcome>("/employee/self/attendance/check-out", input),
  // Start/End Break - same self_mark grant as check-in/check-out, since
  // this is the same kind of self-service action on the caller's own
  // attendance day. No location is sent: unlike a punch, a break carries no
  // geo-fence concern of its own, only server-side timestamps.
  startBreak: () => http.post<BreakOutcome>("/employee/self/attendance/break/start"),
  endBreak: () => http.post<BreakOutcome>("/employee/self/attendance/break/end"),
  // State restoration (section 21/22): called on load/refresh/foreground
  // rather than trusting client-held state, so a page refresh, an app
  // restart, or a token refresh always reflects what the backend actually
  // has.
  currentBreak: () =>
    http.get<{
      break: Break | null;
      attendance?: AttendanceRecord;
      leave_eligibility: LeaveEligibility;
      /** Yesterday's session, still open after midnight, if any. */
      overnight_session?: { date: string; check_in_at: string; source: string } | null;
    }>(
      "/employee/self/attendance/break/current",
    ),
  myPunches: (filters: PageParams & { from?: string; to?: string } = {}) =>
    http.getPage<AttendanceLog>("/employee/self/attendance/punches", filters),

  // ---- Field Workforce ----
  // Which employees these return is decided entirely server-side from the
  // caller's role (Manager -> team, Branch Manager -> their branches, HR
  // Employee -> their org scope, Tenant Admin -> tenant/active branch). No
  // scope parameter is sent, and none would be honoured if it were.
  fieldWorkforce: (params: PageParams & { date?: string } = {}) =>
    http.get<FieldWorkforceResult>("/attendance/field-workforce", params),
  fieldEmployeeActivity: (employeeId: string, from?: string, to?: string) =>
    http.get<FieldEmployeeActivity>(`/attendance/field-workforce/${employeeId}`, { from, to }),
  myFieldActivity: (from?: string, to?: string) =>
    http.get<FieldEmployeeActivity>("/employee/self/attendance/field-work/activity", { from, to }),
  /** The caller's own effective attendance mode (the field-work state read
   * carries it). "check_in_off" means no self check-in from the app or web. */
  myAttendanceMode: () => http.get<{ mode: AttendanceMode }>("/employee/self/attendance/field-work"),

  // ---- Employee attendance mode (People -> Employee -> Attendance Settings) ----
  employeeAttendanceSettings: (employeeId: string) =>
    http.get<EmployeeAttendanceSettings>(`/employees/${employeeId}/attendance-settings`),
  /** null clears the override so the employee inherits from their
   * department, then branch, then the tenant default. */
  setEmployeeAttendanceMode: (employeeId: string, mode: AttendanceMode | null) =>
    http.put<EmployeeAttendanceSettings>(`/employees/${employeeId}/attendance-settings`, { mode }),
};
